import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent, type Ref } from 'react';
import { ApiError } from '../api';
import { highlight, parseQuantityPrefix } from './searchText';
import { useFloating } from './useFloating';

/**
 * One suggestion: `name` gets the matched part in bold, `details` go below it (category, "wolne: 3"), `moreDetails`
 * on a line of its own under them (what the suppliers have). Options with a `group` ("Sprzęt", "Usługi") are shown
 * under a heading per group.
 */
/** A row of `moreDetails`: text, or text with a colour mark (owner 23:09: a supplier's row, `style` sets `--mark`). */
export type PickerDetail = string | { text: string; style: CSSProperties };
export type PickerOption = {
  key: string;
  name: string;
  details: string[];
  /** One line, or several lines (each its own row, owner 23:09). */
  moreDetails?: string | PickerDetail[] | undefined;
  group?: string | undefined;
  /**
   * Owner 2026-10-24 16:19: a supplier's hit — its colour (`markStyle`, sets `--mark` / `--mark-tint`). The row gets a
   * light tint of it at rest and my own yellow highlight in that colour instead (styles.css `.picker-option.supplier`).
   */
  mark?: CSSProperties | undefined;
};

/** Options of one group together, the groups in the order they first appear (the best match stays first). */
function byGroup<T extends PickerOption>(options: T[]): T[] {
  const order: (string | undefined)[] = [];
  for (const o of options) if (!order.includes(o.group)) order.push(o.group);
  return order.flatMap((g) => options.filter((o) => o.group === g));
}

/** How long typing must pause before the search goes out. */
export const SEARCH_DEBOUNCE_MS = 120;

type Props<T extends PickerOption> = {
  /** Visible label of the search box. */
  label: string;
  placeholder?: string | undefined;
  /** Where the suggestions come from; `signal` aborts a search overtaken by newer typing. */
  search: (query: string, signal: AbortSignal) => Promise<T[]>;
  /** The person chose `option` (Enter, click or tap) with the quantity typed in front of the query (default 1). */
  /** `false`: nothing was added (e.g. „Anuluj” in „Dodaj mimo to”): the typed text stays. */
  onPick: (option: T, quantity: number) => Promise<void | boolean> | void | boolean;
  inputRef?: Ref<HTMLInputElement> | undefined;
  featureId?: string | undefined;
  /** The label is read by screen readers only (e.g. a picker in a table row, where the placeholder says enough). */
  labelHidden?: boolean | undefined;
  /**
   * The list is placed `fixed` (`useFloating`, default): no box that scrolls or clips (a wide table, a packlist with one
   * line — owner 2026-10-22 14:42) can hide it, and it opens upwards when there is no room below. `false`: under the box
   * in the page's flow (only where nothing around can clip it).
   */
  floating?: boolean | undefined;
};

/**
 * Quick picker ("szybkie wybieranie"): a combobox (WAI-ARIA pattern) for choosing from a long list with the
 * keyboard only. Typing searches (debounced, older searches cancelled), ↑/↓ choose, Enter takes the chosen
 * suggestion (the first one by default, also when Enter comes before the results), Esc closes the list and then
 * clears the box, Tab moves on as usual. "4 qrp40" or "4x qrp40" picks with quantity 4. Mouse and touch work too.
 */
export function QuickPicker<T extends PickerOption>({ label, placeholder, search, onPick, inputRef, featureId, labelHidden, floating = true }: Props<T>) {
  const id = useId();
  const listId = `${id}-list`;
  const optionId = (i: number) => `${id}-opt-${i}`;
  const [text, setText] = useState('');
  const [results, setResults] = useState<{ query: string; options: T[] } | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  /** The search going out now: Enter for the same query waits for it instead of starting (and cancelling) another. */
  const inflight = useRef<{ query: string; controller: AbortController; promise: Promise<T[] | null> } | null>(null);
  /** The debounced search waiting to go out; Enter clears it, so it cannot cancel the search Enter waits for. */
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { quantity, query } = parseQuantityPrefix(text);

  function clearTimer() {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  }

  function cancel() {
    clearTimer();
    inflight.current?.controller.abort();
    inflight.current = null;
  }

  /** Runs the search for `q` (or joins the one already going out for `q`), cancelling any older one; resolves with its options (null when overtaken). */
  function run(q: string): Promise<T[] | null> {
    const current = inflight.current;
    if (current && current.query === q && !current.controller.signal.aborted) return current.promise;
    current?.controller.abort();
    const mine = new AbortController();
    const promise = (async () => {
      try {
        const options = await search(q, mine.signal);
        if (mine.signal.aborted) return null;
        const grouped = byGroup(options);
        setResults({ query: q, options: grouped });
        setActive(0);
        setError('');
        return grouped;
      } catch (e) {
        if (mine.signal.aborted) return null;
        setError(e instanceof ApiError ? e.message : 'Nie udało się wyszukać');
        return null;
      } finally {
        if (inflight.current?.controller === mine) inflight.current = null;
      }
    })();
    inflight.current = { query: q, controller: mine, promise };
    return promise;
  }

  useEffect(() => {
    clearTimer();
    if (query === '') {
      cancel();
      setResults(null);
      return;
    }
    if (results?.query === query) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      void run(query);
    }, SEARCH_DEBOUNCE_MS);
    return clearTimer;
    // `run` and `results` change on every render; the search depends on the query only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  useEffect(() => cancel, []);

  const options = results?.query === query ? results.options : [];
  const expanded = open && query !== '' && options.length > 0;
  const box = useRef<HTMLDivElement>(null);
  const floatStyle = useFloating(box, expanded, floating, true);

  async function pick(option: T) {
    setBusy(true);
    setError('');
    try {
      if ((await onPick(option, quantity)) === false) return;
      setText('');
      setResults(null);
      setOpen(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się dodać');
    } finally {
      setBusy(false);
    }
  }

  async function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      if (options.length > 0) setActive((a) => (a + (e.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (busy || query === '') return;
      clearTimer();
      // Enter right after typing: the results of this very query, searched now when not there yet
      const current = results?.query === query ? results.options : await run(query);
      const chosen = current?.[results?.query === query ? Math.min(active, current.length - 1) : 0];
      if (chosen) await pick(chosen);
      else if (current) setOpen(true);
    } else if (e.key === 'Escape') {
      if (expanded) {
        // the list closes; Esc must not also close a dialog around the picker
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
      } else if (text !== '') {
        // the text is cleared; a dialog or panel around closes only on the next Escape (ui/Dialog.tsx)
        e.preventDefault();
        setText('');
      }
    }
  }

  const statusText = query === '' ? '' : results?.query !== query ? 'Szukanie…' : options.length === 0 ? 'Nic nie znaleziono.' : `Podpowiedzi: ${options.length}`;

  return (
    <div className="picker" data-feature-id={featureId}>
      <label className={labelHidden ? 'visually-hidden' : 'field'} htmlFor={`${id}-input`}>
        <span>{label}</span>
      </label>
      <div className="picker-box" ref={box}>
        <input
          id={`${id}-input`}
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={expanded ? optionId(active) : undefined}
          autoComplete="off"
          spellCheck={false}
          value={text}
          placeholder={placeholder}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            setError('');
          }}
          onKeyDown={(e) => void onKeyDown(e)}
          onBlur={() => setOpen(false)}
          onFocus={() => setOpen(true)}
        />
        {quantity > 1 && <span className="chip picker-qty">ilość: {quantity}</span>}
        <ul id={listId} role="listbox" aria-label={label} className="picker-list" hidden={!expanded} style={floatStyle}>
          {expanded &&
            options.map((o, i) => [
              o.group !== undefined && o.group !== options[i - 1]?.group && (
                <li key={`group-${o.group}`} role="presentation" className="picker-group">
                  {o.group}
                </li>
              ),
              <li
                key={o.key}
                id={optionId(i)}
                role="option"
                aria-selected={i === active}
                className={`picker-option${o.mark ? ' supplier' : ''}${i === active ? ' active' : ''}`}
                style={o.mark}
                // keeps the focus in the box, so the click does not close the list first
                onMouseDown={(e) => e.preventDefault()}
                onMouseMove={() => setActive(i)}
                onClick={() => void pick(o)}
              >
                <span className="picker-name">
                  {highlight(o.name, query).map((p, k) => (p.match ? <b key={k}>{p.text}</b> : <span key={k}>{p.text}</span>))}
                </span>
                {o.details.length > 0 && <span className="picker-details">{o.details.join(' · ')}</span>}
                {typeof o.moreDetails === 'string' && <span className="picker-details">{o.moreDetails}</span>}
                {Array.isArray(o.moreDetails) && o.moreDetails.map((d, k) => (
                  <span key={k} className={`picker-details picker-details-row${k > 0 && k < o.moreDetails!.length - 1 ? ' indent' : ''}${typeof d === 'string' ? '' : ' marked'}`} style={typeof d === 'string' ? undefined : d.style}>
                    {typeof d === 'string' ? d : d.text}
                  </span>
                ))}
              </li>,
            ])}
        </ul>
      </div>
      <p className="hint" role="status" aria-live="polite">
        {busy ? 'Dodawanie…' : statusText}
      </p>
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
